import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCloneTransitEntities } from '@hooks/transitlLines/useCloneTransitEntities';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import { Form, Row } from '@components/form/Form/Form';
import * as SelectBox from '@components/form/SelectBox/SelectBox';
import { useToastContext } from '@context/toastContext';
import { useEntitiesLoader } from '../../../entities/entities-list/model/useEntitiesLoader';
import { PublicTransitLayerEntityName } from '@api/entities/replancity_PublicTransitLine';
import { EntityServiceName } from '@api/enums/enums';
import { ActionDialogProps, ActionMenuProps } from '@components/ActionMenu/ActionMenu';
import { getFilledFieldsObjOnFormSubmit, isErrorResponse, safelyParseJson } from '@utils/utils';

const CopyRouteToLineActionFabric = ({ id, loadEntitiesFn }: ActionMenuProps) => {
    const CopyRouteToLineAction = ({ handleCloseDialog }: ActionDialogProps) => {
        const { loading, cloneRouteToLine, invertRoute } = useCloneTransitEntities();
        const { lineId } = useParams<any>();
        const { addToast } = useToastContext();
        const { t } = useTranslation();

        const { loading: linesLoading, entities } = useEntitiesLoader({
            entityName: PublicTransitLayerEntityName.LINES,
            serviceName: EntityServiceName.PUBLIC_TRANSIT,
        });

        const linesOptions = entities.map(({ id, name }) => ({
            caption: name,
            value: id,
        }));

        const copyRouteToLine = useCallback(
            async (event, routeId: string) => {
                const formDataObj = getFilledFieldsObjOnFormSubmit(event);
                const { lineId } = formDataObj;

                const resp = await cloneRouteToLine({ routeId, lineId: lineId?.toString() ?? '' });
                if (isErrorResponse(resp)) {
                    const error =
                        typeof resp.error === 'string' ? safelyParseJson(resp.error) : resp.error;
                    addToast(`${error?.['details'] ?? t('common.save-error')}`, 'error');
                } else {
                    await loadEntitiesFn();
                    addToast(`${t('common.save-successful')}`, 'success');
                }

                handleCloseDialog(event);
            },
            [cloneRouteToLine, id]
        );

        return (
            <LoadingBackdrop isVisible={loading || linesLoading} transparent>
                <Form
                    name="copyRouteToLineForm"
                    onSubmit={event => copyRouteToLine(event, id)}
                    submitBtnTitle={t('common.confirm')}
                    // disabled={!selectedPresetId}
                >
                    <Row>
                        <SelectBox.Labeled
                            label={t('transit-lines.copy-route-to-line-select')}
                            name="lineId"
                            options={linesOptions}
                            value={lineId}
                        />
                    </Row>
                </Form>
            </LoadingBackdrop>
        );
    };

    return CopyRouteToLineAction;
};

export default CopyRouteToLineActionFabric;
